import React from "react";
import { Link } from "react-router-dom";
import { formatCount } from "../dashboard/incomeLabels";

// Shared by the Ads pages: the campaigns (Campaigns.js) and their report
// (AdReport.js). What the kiosks do with them is cloud-print-v4's
// docs/kiosk-ads-mn.md.

// As the backend's utils/adCampaigns.js takes them
export const MAX_MEDIA_MB = 25;
export const MAX_WEIGHT = 10;
export const DEFAULT_IMAGE_SEC = 10;
export const MEDIA_ACCEPT = ".mp4,.webm,.jpg,.jpeg,.png,.webp";
export const isMediaName = (name) => /\.(mp4|webm|jpe?g|png|webp)$/i.test(name);
export const isVideoName = (name) => /\.(mp4|webm)$/i.test(name);

// The ad built into the kiosk, which it plays when no campaign is on
export const HOUSE_CAMPAIGN = "house";

export const card = "bg-white border border-gray-100 rounded-xl shadow-xs dark:bg-gray-800 dark:border-gray-700";

const BADGES = {
	green: "text-green-700 bg-green-100 dark:text-green-100 dark:bg-green-700",
	blue: "text-blue-700 bg-blue-100 dark:text-blue-100 dark:bg-blue-700",
	yellow: "text-yellow-700 bg-yellow-100 dark:text-yellow-100 dark:bg-yellow-700",
	gray: "text-gray-700 bg-gray-100 dark:text-gray-100 dark:bg-gray-700",
};

const STATUSES = {
	live: { label: "Цацагдаж байна", badge: BADGES.green },
	scheduled: { label: "Эхлээгүй", badge: BADGES.blue },
	offHours: { label: "Хуваарийн гадна", badge: BADGES.blue },
	empty: { label: "Бүтээлгүй", badge: BADGES.yellow },
	ended: { label: "Дууссан", badge: BADGES.gray },
	off: { label: "Унтраастай", badge: BADGES.gray },
};

// What a campaign does now. live, scheduled and offHours are the ones the kiosks
// are sent. Whether it is in its hours now the backend tells (onAir), by
// Ulaanbaatar time whatever the browser's.
export const campaignStatus = (campaign, now = new Date()) => {
	const status = (key) => ({ key, ...STATUSES[key] });
	if (!campaign.enabled) return status("off");
	if (campaign.endAt && new Date(campaign.endAt) <= now) return status("ended");
	if (!campaign.creatives.some((creative) => creative.enabled)) return status("empty");
	if (campaign.startAt && new Date(campaign.startAt) > now) return status("scheduled");
	if (campaign.onAir === false) return status("offHours");
	return status("live");
};

// Monday first, as the week is read here; value is Date.getDay()'s
export const WEEKDAYS = [
	{ value: 1, label: "Да" },
	{ value: 2, label: "Мя" },
	{ value: 3, label: "Лх" },
	{ value: 4, label: "Пү" },
	{ value: 5, label: "Ба" },
	{ value: 6, label: "Бя" },
	{ value: 0, label: "Ня" },
];
export const MAX_HOUR_RANGES = 4;

// The days and hours a campaign plays, in words; "" for all day, every day
export const scheduleText = (schedule) => {
	const days = [...schedule.days].sort((a, b) => a - b).join(",");
	const dayText =
		days === ""
			? ""
			: days === "1,2,3,4,5"
			? "Ажлын өдөр"
			: days === "0,6"
			? "Амралтын өдөр"
			: WEEKDAYS.filter((day) => schedule.days.includes(day.value))
					.map((day) => day.label)
					.join(", ");
	const hourText = schedule.hours.map(({ from, to }) => `${from}–${to}`).join(", ");
	return [dayText, hourText].filter(Boolean).join(" · ");
};

// Whether a kiosk has its list (the backend's deliveryOf)
export const DELIVERY = {
	ready: {
		label: "Бэлэн",
		dot: "bg-green-500",
		hint: "Жагсаалтын бүх файл киоскт татагдсан",
	},
	partial: {
		label: "Дутуу татагдсан",
		dot: "bg-yellow-400",
		hint: "Зарим файл татагдаагүй. Киоск heartbeat бүрд дахин оролдоно.",
	},
	pending: {
		label: "Шинэчлэгдэж байна",
		dot: "bg-blue-400",
		hint: "Киоск дараагийн heartbeat-ээр (5 мин орчим) шинэ жагсаалтаа татна",
	},
	unsupported: {
		label: "Зар дэмжихгүй",
		dot: "bg-gray-400",
		hint: "Киоскийн программ зарын өмнөх хувилбар. Шинэчлэгдэх хүртэл өөрийн зараа тоглуулна.",
	},
};
export const deliveryOf = (printer) => DELIVERY[printer.delivery] || DELIVERY.unsupported;

// The kiosks a campaign is sent to; none picked is every kiosk
export const targetsOf = (campaign, printers) =>
	campaign.printers.length === 0 ? printers : printers.filter((printer) => campaign.printers.includes(printer._id));

// The days a campaign runs, both included
export const periodText = (campaign) => {
	if (!campaign.startDay && !campaign.endDay) return "Хугацаагүй";
	if (!campaign.endDay) return `${campaign.startDay}-с эхлэн`;
	if (!campaign.startDay) return `${campaign.endDay} хүртэл`;
	return campaign.startDay === campaign.endDay
		? campaign.startDay
		: `${campaign.startDay} — ${campaign.endDay}`;
};

// Seconds as people say them: 45 сек, 12 мин, 3 цаг 05 мин
export const durationText = (seconds) => {
	const total = Math.round(seconds || 0);
	if (total < 60) return `${total} сек`;
	const minutes = Math.round(total / 60);
	if (minutes < 60) return `${minutes} мин`;
	const hours = Math.floor(minutes / 60);
	return `${formatCount(hours)} цаг ${String(minutes % 60).padStart(2, "0")} мин`;
};

export const mbText = (bytes) =>
	bytes < 1024 * 1024
		? `${Math.max(1, Math.round(bytes / 1024))} KB`
		: `${(bytes / (1024 * 1024)).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} MB`;

export const share = (part, whole) => (whole > 0 ? Math.round((part / whole) * 100) : 0);

export const SIZE_LABELS = {
	small: "Буланд",
	large: "Бүтэн дэлгэцээр",
};

export const PAGE_LABELS = {
	home: "Нүүр",
	print: "Хэвлэх",
	etugen: "Этүгэн",
	"etugen-print": "Этүгэн хэвлэх",
};

// 16:9 fills the corner and a landscape kiosk's screen, 9:16 an upright one's.
// Anything else is shown with its sides filled by its own blurred colours.
export const shapeNote = (creative) => {
	if (!creative.width || !creative.height) return null;
	const ratio = creative.width / creative.height;
	if (Math.abs(ratio - 16 / 9) < 0.03) return null;
	if (Math.abs(ratio - 9 / 16) < 0.03) return "9:16 · босоо киоскийн бүтэн дэлгэцэнд";
	return "16:9 биш · хажуу тал нь бүдгэрч дүүрнэ";
};

// The two pages' tabs
const TABS = [
	{ to: "/ads", label: "Кампанит ажил", icon: "fa-bullhorn" },
	{ to: "/ad-report", label: "Тайлан", icon: "fa-chart-bar" },
];

export const AdTabs = ({ active }) => (
	<nav className="flex gap-2 mb-6 border-b border-gray-200 dark:border-gray-700" aria-label="Реклам">
		{TABS.map((tab) => (
			<Link
				key={tab.to}
				to={tab.to}
				aria-current={tab.to === active ? "page" : undefined}
				className={
					"inline-flex items-center px-3 py-2 -mb-px text-sm font-medium border-b-2 focus:outline-none " +
					(tab.to === active
						? "text-purple-600 border-purple-600 dark:text-purple-300 dark:border-purple-300"
						: "text-gray-500 border-transparent hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200")
				}
			>
				<i className={"mr-2 fas " + tab.icon} />
				{tab.label}
			</Link>
		))}
	</nav>
);
